import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { Pencil, UserPlus } from 'lucide-react';
import { adminQueries } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import {
  ADDRESS_KIND_LABELS,
  CONSENT_LABELS,
  CONTACT_KIND_LABELS,
  formatDate,
  formatDateTime,
  hasPermission,
  labelOf,
  PARTY_SOURCE_LABELS,
  PARTY_TYPE_LABELS,
} from '@blimo/shared';
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, DescriptionList, ErrorState, PageHeader, StatusBadge } from '@blimo/ui';
import { DetailSkeleton, ExternalReferences, ReadOnlyHint, SourceBadge } from '../../components/data.jsx';
import { isImportedParty, isPartyEditable } from './party-utils.js';
import { HistoryPanel } from '../../components/history-panel.jsx';
import { UserFormSheet } from '../users/user-form-sheet.jsx';
import { DeletePartyBlockedAlert, DeletePartyButton } from './delete-party.jsx';

export function PartyDetailPage() {
  const api = useApi();
  const { data: me } = useMe();
  const { partyId } = useParams({ strict: false });
  const { data: party, error, isPending, refetch } = useQuery(adminQueries.party(api, partyId));
  const [accessOpen, setAccessOpen] = useState(false);
  const [deleteBlocked, setDeleteBlocked] = useState(null);
  const breadcrumb = (
    <Link to="/personen" className="hover:underline">
      Personen & Organisationen
    </Link>
  );

  if (error) {
    return (
      <>
        <PageHeader title="Person / Organisation" breadcrumb={breadcrumb} />
        <ErrorState
          error={error}
          onRetry={() => refetch()}
          notFoundTitle="Person bzw. Organisation nicht gefunden"
          action={
            <Button asChild variant="outline" size="sm">
              <Link to="/personen">Zur Liste</Link>
            </Button>
          }
        />
      </>
    );
  }
  if (isPending) return <DetailSkeleton />;

  const editable = isPartyEditable(party);
  const canUpdate = hasPermission(me, 'parties.update');
  const isPerson = party.type === 'person';
  const canDelete = hasPermission(me, 'parties.delete');

  return (
    <>
      <PageHeader
        breadcrumb={breadcrumb}
        title={party.display_name || `#${party.id}`}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary">{labelOf(PARTY_TYPE_LABELS, party.type)}</Badge>
            <SourceBadge source={party.source} />
            {party.archived_at && <Badge variant="neutral">Archiviert</Badge>}
          </span>
        }
        actions={
          (editable || canDelete) && (
            <>
              {editable && (
                <Button asChild size="sm" variant="outline">
                  <Link to="/personen/$partyId/bearbeiten" params={{ partyId: String(party.id) }}>
                    <Pencil aria-hidden="true" /> Bearbeiten
                  </Link>
                </Button>
              )}
              {canDelete && <DeletePartyButton party={party} onBlocked={setDeleteBlocked} />}
            </>
          )
        }
      />
      <DeletePartyBlockedAlert blocked={deleteBlocked} />
      {!editable && canUpdate && (
        <div className="mb-3">
          <ReadOnlyHint>
            {isImportedParty(party) ? (
              <>
                Dieser Datensatz wird in {labelOf(PARTY_SOURCE_LABELS, party.source)} gepflegt und regelmäßig importiert. Änderungen bitte im Quellsystem
                vornehmen – hier ist er schreibgeschützt.
              </>
            ) : (
              'Dieser Datensatz ist für Sie schreibgeschützt.'
            )}
          </ReadOnlyHint>
        </div>
      )}
      <div className="grid gap-3 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle as="h2">Stammdaten</CardTitle>
          </CardHeader>
          <CardContent>
            <DescriptionList
              items={
                isPerson
                  ? [
                      { label: 'Anrede', value: party.salutation },
                      { label: 'Titel', value: party.title },
                      { label: 'Vorname', value: party.first_name },
                      { label: 'Nachname', value: party.last_name },
                      { label: 'Organisation', value: party.organization_name },
                      { label: 'Briefanrede', value: party.letter_salutation },
                      { label: 'Geburtstag', value: party.birthday ? formatDate(party.birthday) : null },
                      { label: 'Notizen', value: party.notes ? <span className="whitespace-pre-line">{party.notes}</span> : null },
                    ]
                  : [
                      { label: 'Name', value: party.organization_name },
                      { label: 'Briefanrede', value: party.letter_salutation },
                      { label: 'Notizen', value: party.notes ? <span className="whitespace-pre-line">{party.notes}</span> : null },
                    ]
              }
            />
          </CardContent>
        </Card>
        <ContactsCard contacts={party.contacts} />
        <AddressesCard addresses={party.addresses} />
        <Card>
          <CardHeader>
            <CardTitle as="h2">Einwilligungen</CardTitle>
          </CardHeader>
          <CardContent>
            {(party.consents ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">Keine Einwilligungen erfasst.</p>
            ) : (
              <ul className="flex flex-col gap-1.5 text-sm">
                {party.consents.map((c) => (
                  <li key={c.consent} className="flex flex-wrap items-center gap-2">
                    {c.revoked_at ? <Badge variant="neutral">Widerrufen</Badge> : <Badge variant="success">Erteilt</Badge>}
                    <span>{labelOf(CONSENT_LABELS, c.consent)}</span>
                    <span className="numeric text-xs text-muted-foreground">
                      {c.revoked_at ? `widerrufen am ${formatDateTime(c.revoked_at)}` : c.granted_at ? `seit ${formatDateTime(c.granted_at)}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle as="h2">Benutzerkonto</CardTitle>
            <CardDescription>Zugang zum Kundenportal bzw. zur Verwaltung</CardDescription>
          </CardHeader>
          <CardContent>
            {party.user ? (
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <StatusBadge domain="user" status={party.user.status} />
                {hasPermission(me, 'users.view') ? (
                  <Link to="/administration/benutzer/$userId" params={{ userId: String(party.user.id) }} className="break-all text-primary hover:underline">
                    {party.user.email}
                  </Link>
                ) : (
                  <span className="break-all">{party.user.email}</span>
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                <p className="text-sm text-muted-foreground">Kein Benutzerkonto verknüpft.</p>
                {hasPermission(me, 'users.manage') && (
                  <Button type="button" variant="outline" size="sm" className="self-start" onClick={() => setAccessOpen(true)}>
                    <UserPlus aria-hidden="true" /> Zugang anlegen
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
        {hasPermission(me, 'users.manage') && !party.user && (
          <UserFormSheet
            user={null}
            party={{
              id: party.id,
              display_name: party.display_name,
              email: (party.contacts ?? []).filter((c) => c.kind === 'email').sort((a, b) => Number(b.is_primary) - Number(a.is_primary))[0]?.value ?? null,
            }}
            open={accessOpen}
            onOpenChange={setAccessOpen}
            onCreated={() => refetch()}
          />
        )}
        <Card>
          <CardHeader>
            <CardTitle as="h2">Externe Schlüssel</CardTitle>
          </CardHeader>
          <CardContent>
            <ExternalReferences references={party.external_references} />
          </CardContent>
        </Card>
        <HistoryPanel subjectType="party" subjectId={party.id} />
      </div>
    </>
  );
}

function ContactsCard({ contacts = [] }) {
  const groups = Object.keys(CONTACT_KIND_LABELS)
    .concat([...new Set(contacts.map((c) => c.kind))].filter((k) => !(k in CONTACT_KIND_LABELS)))
    .map((kind) => ({ kind, items: contacts.filter((c) => c.kind === kind) }))
    .filter((g) => g.items.length > 0);
  return (
    <Card>
      <CardHeader>
        <CardTitle as="h2">Kontakt</CardTitle>
      </CardHeader>
      <CardContent>
        {groups.length === 0 ? (
          <p className="text-sm text-muted-foreground">Keine Kontaktdaten hinterlegt.</p>
        ) : (
          <dl className="flex flex-col gap-3 text-sm">
            {groups.map((group) => (
              <div key={group.kind}>
                <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{labelOf(CONTACT_KIND_LABELS, group.kind)}</dt>
                {group.items.map((c) => (
                  <dd key={c.id ?? c.value} className="flex flex-wrap items-center gap-2 py-0.5">
                    <ContactValue contact={c} />
                    {c.label && <span className="text-xs text-muted-foreground">({c.label})</span>}
                    {c.is_primary && <Badge variant="secondary">Primär</Badge>}
                    {c.source && c.source !== 'manual' && <span className="text-xs text-muted-foreground">aus {labelOf(PARTY_SOURCE_LABELS, c.source)}</span>}
                  </dd>
                ))}
              </div>
            ))}
          </dl>
        )}
      </CardContent>
    </Card>
  );
}

function ContactValue({ contact }) {
  if (contact.kind === 'email')
    return (
      <a href={`mailto:${contact.value}`} className="break-all text-primary hover:underline">
        {contact.value}
      </a>
    );
  if (['phone', 'mobile'].includes(contact.kind))
    return (
      <a href={`tel:${contact.value.replace(/[^\d+]/g, '')}`} className="numeric text-primary hover:underline">
        {contact.value}
      </a>
    );
  return <span className="break-all">{contact.value}</span>;
}

function AddressesCard({ addresses = [] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle as="h2">Adressen</CardTitle>
      </CardHeader>
      <CardContent>
        {addresses.length === 0 ? (
          <p className="text-sm text-muted-foreground">Keine Adressen hinterlegt.</p>
        ) : (
          <ul className="flex flex-col gap-3 text-sm">
            {addresses.map((a) => (
              <li key={a.id}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{labelOf(ADDRESS_KIND_LABELS, a.kind)}</span>
                  {a.valid_to && <Badge variant="neutral">bis {formatDate(a.valid_to)}</Badge>}
                  {a.source && a.source !== 'manual' && <span className="text-xs text-muted-foreground">aus {labelOf(PARTY_SOURCE_LABELS, a.source)}</span>}
                </div>
                <address className="not-italic">
                  {[a.street, a.house_number].filter(Boolean).join(' ')}
                  <br />
                  {[a.postal_code, a.city].filter(Boolean).join(' ')}
                  {a.country && a.country !== 'DE' ? `, ${a.country}` : ''}
                </address>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
